import React, {
  lazy,
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import {
  Navigate,
  Route,
  Routes,
  generatePath,
  useLocation,
  useNavigate,
  useParams
} from "react-router-dom";
import i18next from "i18next";
import { getUA } from "react-device-detect";
import { Action } from "history";
import { useDispatch, useSelector } from "react-redux";
import { CombinedReducersType, ConfigActions } from "@redux/reducer";
import {
  BRAND_LIST,
  COUNTRY,
  GenderObjType,
  RouterUrlType,
  StylingType,
  WishObjType
} from "@type/index";
import {
  FR_UserAgent,
  guAndroidPattern,
  guIOSPattern,
  UQ_JP_JA_Android_UserAgent,
  UQ_JP_JA_Ios_UserAgent,
  UQ_US_EN_Android_UserAgent,
  UQ_US_EN_Ios_UserAgent
} from "@config/common";
import { warmFirebaseConfig } from "@util/firebase";
import { getConfirmation } from "@util/net/interceptors";
import {
  convertRegionNameForWording,
  isEUCountry,
  isEUMinorCountry
} from "@util/country";
import { history } from "@common/CustomRouter";
import { LoadingIndicator } from "@common/DynamicUikit";
import {
  PublicHeader,
  PublicFooter,
  ImageIntersectionObserverProvider
} from "@common/index";
import ErrorPage from "./ErrorPage";
import NotFoundPage from "./DetailStyling/notFoundPage";

const TopStyling = lazy(() => import("./TopStyling"));
const DetailStyling = lazy(() => import("./DetailStyling"));
const PersonalPage = lazy(() => import("./PersonalPage"));
const UserList = lazy(() => import("./UserListPage"));

const SCROLL_POSITION_KEY = "scrollPosition";
const MAX_SCROLL_POSITIONS = 50;

// Cache of scroll positions keyed by location key, persisted to sessionStorage
// so they survive page reloads and can be restored when navigating back.
let scrollPositionMap: Record<string, number> | null = null;

const getScrollPositionMap = (): Record<string, number> => {
  if (scrollPositionMap) {
    return scrollPositionMap;
  }
  try {
    scrollPositionMap = JSON.parse(
      sessionStorage.getItem(SCROLL_POSITION_KEY) || "{}"
    );
  } catch (e) {
    // ignore storage read errors
  }
  scrollPositionMap = scrollPositionMap || {};
  return scrollPositionMap;
};

// Save the scroll position of a page (identified by its location key).
// Keep at most MAX_SCROLL_POSITIONS entries, evicting the oldest inserted
// keys so the sessionStorage map does not grow unboundedly.
const saveScrollPosition = (key: string, value: number) => {
  const map = getScrollPositionMap();
  const isNewKey = !Object.prototype.hasOwnProperty.call(map, key);
  map[key] = value;
  if (isNewKey) {
    const keys = Object.keys(map);
    keys
      .slice(0, Math.max(0, keys.length - MAX_SCROLL_POSITIONS))
      .forEach((oldKey) => delete map[oldKey]);
  }
  try {
    sessionStorage.setItem(SCROLL_POSITION_KEY, JSON.stringify(map));
  } catch (e) {
    // ignore storage write errors
  }
};

// Get the saved scroll position of a page (identified by its location key).
const getScrollPosition = (key: string): number =>
  getScrollPositionMap()[key] || 0;

const App = () => {
  const dispatch = useDispatch();
  const { language, region, type } = useParams();
  const params = useParams();
  const navigate = useNavigate();
  const config = useSelector((store: CombinedReducersType) => store.config);
  const uLocation = useLocation();
  const [currHist, setCurrHist] = useState<{
    action: Action;
    location: any;
  }>({
    action: "",
    location: {}
  } as any);
  const prevPathnameRef = useRef(uLocation.pathname);
  const currentKeyRef = useRef(uLocation.key);
  const restoreScrollRef = useRef<number | null>(null);

  // UQNL & UQBE & EU minor regions only have officialstyling -> stylehint should redirect to official
  useEffect(() => {
    if (
      (region === COUNTRY.NL ||
        region === COUNTRY.BE ||
        (region && isEUMinorCountry(region))) &&
      type === StylingType.stylehint
    ) {
      const url = generatePath(
        "/:region/:language/stylingbook/officialstyling/women",
        {
          ...params
        } as RouterUrlType
      );
      navigate(url, { replace: true });
    }
  }, [navigate, params, region, type]);

  useEffect(() => {
    window.history.scrollRestoration = "manual";
  }, []);

  useEffect(() => {
    const scrolled = () => {
      saveScrollPosition(currentKeyRef.current, window.scrollY);
    };
    window.addEventListener("scroll", scrolled);
    return () => {
      window.removeEventListener("scroll", scrolled);
    };
  }, []);

  useEffect(
    () =>
      history.listen(({ location, action }: any) => {
        let _location = location;
        // Before leaving the current page, record its scroll position
        if (action === "PUSH" || action === "REPLACE") {
          saveScrollPosition(currentKeyRef.current, window.scrollY);
        }
        currentKeyRef.current = location.key;
        if (action === "POP") {
          const yscroll = getScrollPosition(location.key);
          _location = {
            ...location,
            state: {
              forward: {
                yscroll: yscroll
              },
              ...location.state
            }
          };
        }
        setCurrHist((prev) => ({
          ...prev,
          action,
          location: { ..._location }
        }));
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useLayoutEffect(() => {
    // clear a pending scroll restore timer from a previous navigation
    if (restoreScrollRef.current) {
      clearTimeout(restoreScrollRef.current);
      restoreScrollRef.current = null;
    }
    const { action, location } = currHist;
    if (action && action === "POP") {
      if (location.state?.forward) {
        const yscroll = parseFloat(location.state.forward.yscroll);
        if (!Number.isNaN(yscroll) && yscroll >= 0) {
          // restore the scroll position once the page content is rendered;
          // on each attempt scroll to the target as soon as the page is tall
          // enough, then keep re-applying until the scroll actually stays at
          // the target: the browser may reset the scroll right after a history
          // traversal, and some pages reset and refetch their data on back
          // navigation (changing the page height in stages).
          // A saved position of 0 (left the page at the top without scrolling)
          // must also be restored, otherwise the scroll position of the page we
          // are navigating away from would remain.
          const restoreScroll = (
            target: number,
            attempt: number,
            wasAtTarget: boolean
          ) => {
            const maxScroll =
              Math.max(
                document.body.scrollHeight,
                document.documentElement.scrollHeight
              ) - window.innerHeight;
            if (target === 0 || maxScroll >= target) {
              window.scrollTo(0, target);
            }
            const isAtTarget = Math.abs(window.scrollY - target) < 2;
            if (isAtTarget && wasAtTarget) {
              restoreScrollRef.current = null;
              return;
            }
            if (attempt >= 60) {
              restoreScrollRef.current = null;
              return;
            }
            restoreScrollRef.current = window.setTimeout(() => {
              restoreScroll(target, attempt + 1, isAtTarget);
            }, 100);
          };
          restoreScroll(yscroll, 0, false);
        }
      }
      if (location.pathname) {
        prevPathnameRef.current = location.pathname;
      }
      return;
    }

    const nextPathname = location.pathname || "";
    const prevPathname = prevPathnameRef.current;

    // Scroll to the top only when the path changes
    // when using filter, only modify the query without scrolling to the top
    if (nextPathname && nextPathname !== prevPathname) {
      window.scrollTo(0, 0);
    }

    if (nextPathname) {
      prevPathnameRef.current = nextPathname;
    }
  }, [currHist]);

  // set current userAgent
  // set current region and language
  useEffect(() => {
    i18next.changeLanguage(
      `${language}-${convertRegionNameForWording(region || "")}`
    );
    if (
      getUA.indexOf(UQ_JP_JA_Ios_UserAgent) > -1 ||
      getUA.indexOf(UQ_JP_JA_Android_UserAgent) > -1 ||
      getUA.indexOf(UQ_US_EN_Ios_UserAgent) > -1 ||
      getUA.indexOf(UQ_US_EN_Android_UserAgent) > -1 ||
      getUA.indexOf(FR_UserAgent) > -1 ||
      guIOSPattern.test(getUA) ||
      guAndroidPattern.test(getUA)
    ) {
      dispatch(ConfigActions.setIsApp(true));
    }
  }, [dispatch, language, region]);

  useEffect(() => {
    if (config.showSlide) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [config.showSlide]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    dispatch(ConfigActions.setUrlParams(searchParams.toString() || ""));
    const hasFeatureId = searchParams.has("featureId");
    const hasCoordinate = searchParams.has("coordinate");
    dispatch(
      ConfigActions.setUrlTotalParams(
        (hasFeatureId ? 1 : 0) + (hasCoordinate ? 1 : 0)
      )
    );
  }, [dispatch]);

  // Get gender data from Firebase
  useEffect(() => {
    // Render with safe defaults first; Remote Config can refine these values later.
    const default_value_gender: GenderObjType =
      config.brand_name === BRAND_LIST.PL
        ? { women: true, men: true, kids: false, baby: false }
        : { women: true, men: true, kids: true, baby: true };
    const default_value_sb_wish: WishObjType = { icon: true, count: true };
    const default_value_sh_wish: WishObjType = { icon: true, count: true };
    const defaultConfig = {
      sb_tab_gender: default_value_gender,
      sh_tab_gender: default_value_gender,
      sb_wish: default_value_sb_wish,
      sh_wish: default_value_sh_wish,
      price_change_history: !!isEUCountry(region || "")
    };

    dispatch(ConfigActions.setFirebase(defaultConfig));

    let isCurrent = true;
    warmFirebaseConfig().then((firebaseConfig) => {
      if (!isCurrent || firebaseConfig === null) {
        return;
      }

      const remoteConfig = {
        ...defaultConfig,
        sb_tab_gender:
          firebaseConfig.web_tab_gender?.sb || defaultConfig.sb_tab_gender,
        sh_tab_gender:
          firebaseConfig.web_tab_gender?.sh || defaultConfig.sh_tab_gender,
        sb_wish: firebaseConfig.web_wish?.sb || defaultConfig.sb_wish,
        sh_wish: firebaseConfig.web_wish?.sh || defaultConfig.sh_wish,
        price_change_history:
          firebaseConfig.price_change_history === ""
            ? defaultConfig.price_change_history
            : firebaseConfig.price_change_history === "true"
      };

      dispatch(ConfigActions.setFirebase(remoteConfig));
    });

    return () => {
      isCurrent = false;
    };
  }, [config.brand_name, dispatch, region]);

  // Cancel all pending requests when change router url
  useEffect(
    () => () => {
      getConfirmation();
    },
    [uLocation.pathname]
  );

  const elementRouteGender = (gender: keyof GenderObjType) => {
    const obj =
      type === StylingType.official
        ? config.firebaseData.sb_tab_gender
        : config.firebaseData.sh_tab_gender;
    if (obj[gender]) {
      return <TopStyling />;
    } else {
      return <Navigate replace to="women" />;
    }
  };

  return (
    <ImageIntersectionObserverProvider>
      <PublicHeader />
      {type === StylingType.official || type === StylingType.stylehint ? (
        <main>
          <Suspense
            fallback={
              <div style={{ minHeight: "85vh" }}>
                <LoadingIndicator variant="normal" />
              </div>
            }
          >
            <Routes>
              <Route path="women" element={elementRouteGender("women")} />
              <Route path="men" element={elementRouteGender("men")} />
              <Route path="kids" element={elementRouteGender("kids")} />
              <Route path="baby" element={elementRouteGender("baby")} />
              <Route path="userlist/:gender" element={<UserList />} />
              <Route path="not-found" element={<NotFoundPage type={type} />} />
              <Route path=":stylingId" element={<DetailStyling />} />
              {type === StylingType.stylehint && (
                <Route path="user/:authorId" element={<PersonalPage />} />
              )}
              <Route path="*" element={<Navigate replace to="women" />} />
            </Routes>
          </Suspense>
        </main>
      ) : (
        <ErrorPage status={404} />
      )}
      <PublicFooter />
    </ImageIntersectionObserverProvider>
  );
};

export default App;
